import React from 'react';
import pick from 'lodash/pick';
import ActivityStatusBar from '../../../components/activity-status-bar/activity-status-bar';
import ActivityPercentageBar from '../../../components/activity-percentage-bar/activity-percentage-bar';
import TableHeader from '../../../components/table-header/table-header';
import css from './student.scss';
import { getTranslationKey } from '../../../helpers/locale-helpers';
import {
    NUM_ACTIVITIES,
    RESULTS,
    ACTIVITY,
    ATTEMPTS
} from '../../../components/report-filters/filter-constants';

const ALL_RESULTS_TAB = 0;
const TEST_RESULTS_TAB = 1;

const TOPIC_NAME = 'name';
const TOPIC_RESULTS = 'topicActivityResults';
const TOTAL_ACTIVITIES = 'totalActivities';
const ACTIVITY_NAME = 'name';
const ACTIVITY_RESULTS = 'result';
const ACTIVITY_ATTEMPTS = 'attempts';
const NUM_ACTIVITIES_ID = 'numActivities';
const TABLE_TABS = 'tableTabs';
const NUM_ATTEMPTS_ID = 'numAttempts';
const TEST_ATTEMPTS = 'attempts';
const TEST_RESULTS = 'result';
const TABLE_TITLE = 'tableTitle';
const CLASS_NAME = 'className';

/**
 * Config for the 'All results' tab of the student activity mastery table
 */
const ALL_RESULTS_TABLE_CONFIG = {
    colGroups: generateColGroups(
        [TOPIC_NAME, TOPIC_RESULTS, TOTAL_ACTIVITIES],
        {
            id: NUM_ACTIVITIES_ID,
            name: getTranslationKey(NUM_ACTIVITIES),
            doTranslation: true
        }
    ),
    cols: [
        {
            id: TOPIC_NAME,
            name: '', // this is set to course name in Student component
            truncate: true,
            headerTruncate: true,
            classes: css['topic-name']
        },
        {
            id: TOPIC_RESULTS,
            name: RESULTS,
            classes: css['activity-results'],
            headerTransform: () => null, // this is set to use ActivityStatusBar in the Student component
            transform: (row) => {
                const activityMastery = pick(row, [
                    'scoreBand',
                    'goldBars',
                    'totalActivities'
                ]);
                return <ActivityStatusBar activityMastery={activityMastery} />;
            }
        },
        {
            id: TOTAL_ACTIVITIES,
            name: '', // this is set to total number of activities in the course in Student component
            transform: (row) => {
                let { totalActivities } = row;
                if (row.totalAssignedActivities) {
                    totalActivities += ` (${row.totalAssignedActivities})`;
                }
                return totalActivities;
            }
        }
    ],
    nestedData: {
        id: 'activityResultDetails',
        showHeaders: true,
        collapse: {
            enable: true,
            expandedClass: css.expanded
        },
        cols: [
            {
                name: getTranslationKey(ACTIVITY),
                doTranslation: true,
                id: ACTIVITY_NAME,
                classes: css['activity-name'],
                truncate: true
            },
            {
                name: getTranslationKey(RESULTS),
                doTranslation: true,
                id: ACTIVITY_RESULTS,
                classes: css['activity-results'],
                transform: (row) => (
                    <ActivityPercentageBar
                        hasGoldBar={row.hasGoldBar}
                        percentage={row.result}
                    />
                )
            },
            {
                name: getTranslationKey(ATTEMPTS),
                doTranslation: true,
                id: ACTIVITY_ATTEMPTS,
                classes: css.attempts
            }
        ]
    },
    emptyDataMessage: 'No results found'
};

/**
 * Config for the 'Test results' tab of the student activity mastery table
 */
const TEST_RESULTS_TABLE_CONFIG = {
    colGroups: generateColGroups([TOPIC_NAME, TEST_RESULTS, TEST_ATTEMPTS], {
        id: NUM_ATTEMPTS_ID,
        name: 'NO. OF ATTEMPTS',
        doTranslation: true
    }),
    cols: [
        {
            id: TOPIC_NAME,
            name: '', // this is set to course name in Student component
            truncate: true,
            headerTruncate: true,
            classes: css['test-topic-name']
        },
        {
            id: TEST_RESULTS,
            classes: css['test-results'],
            transform: (row) => (
                <ActivityPercentageBar
                    hasGoldBar={row.hasGoldBar}
                    percentage={row.result}
                />
            )
        },
        {
            id: TEST_ATTEMPTS,
            name: '', // this is set to total number of attempts in Student component
            classes: css['total-activities']
        }
    ],
    nestedData: {
        collapse: {
            enable: true
        }
    },
    emptyDataMessage: 'No test results found'
};

/**
 * Generates the colGroup config for the All results/Test results table config
 * Uses provided cols to assign cols to a col group and adds the totalColGroup as the last col group
 * in the first col group row
 */
function generateColGroups(cols, totalColGroup) {
    return [
        [
            {
                id: TABLE_TITLE,
                name: 'Activity Mastery',
                headerTransform: () => (
                    <TableHeader
                        title="Table Header - Activity Mastery"
                        translate
                    />
                ),
                colIds: [cols[0], cols[1]]
            },
            {
                ...totalColGroup,
                colIds: [cols[2]],
                classes: css['total-activities']
            }
        ],
        [
            {
                id: CLASS_NAME,
                name: '', // this is set to the class name in Student component
                truncate: true,
                headerTruncate: true,
                classes: css['class-name'],
                colIds: [cols[0]]
            },
            {
                id: TABLE_TABS,
                name: 'Table Tabs',
                classes: css['table-tabs'],
                colIds: [cols[1], cols[2]],
                headerTransform: () => null // this is set in Student component
            }
        ]
    ];
}

export {
    ALL_RESULTS_TAB,
    TEST_RESULTS_TAB,
    CLASS_NAME,
    TABLE_TABS,
    TOPIC_NAME,
    TOPIC_RESULTS,
    ACTIVITY_RESULTS,
    TOTAL_ACTIVITIES,
    TEST_ATTEMPTS,
    ALL_RESULTS_TABLE_CONFIG,
    TEST_RESULTS_TABLE_CONFIG
};



// WEBPACK FOOTER //
// ./containers/activity-mastery/student/student-constants.js